import React from 'react';
import { C } from '../constants/theme';

const TABLE_STYLES = `
  .cms-table-outer {
    overflow-x: auto;
    overflow-y: auto;
    max-height: 60vh;
    -webkit-overflow-scrolling: touch;
  }

  .cms-table-outer::-webkit-scrollbar {
    height: 10px;
    width: 6px;
  }

  .cms-table-outer::-webkit-scrollbar-track {
    background: #F5F3FC;
    border-radius: 10px;
  }

  .cms-table-outer::-webkit-scrollbar-thumb {
    background: #A38EDB;
    border-radius: 10px;
    border: 2.5px solid #F5F3FC;
    min-width: 60px;
  }

  .cms-table-outer::-webkit-scrollbar-thumb:hover { background: #8B6ED1; }
  .cms-table-outer::-webkit-scrollbar-thumb:active { background: #6F4EC4; }

  .cms-tr { transition: background 0.15s ease; }
  .cms-tr:hover td { background: #FAFBFF !important; }
  .cms-tr:hover td.sticky-action { background: #FAFBFF !important; }
  .cms-td { position: relative; }

  /* ── viewEnable cell ── */
  .cms-view-cell {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    padding: 4px 10px 4px 6px;
    border-radius: 8px;
    transition: background 0.15s, box-shadow 0.15s;
    max-width: 220px;
  }
  .cms-view-cell:hover {
    background: #EDE8FA;
    box-shadow: 0 0 0 1.5px #A38EDB;
  }
  .cms-view-cell:hover .cms-view-icon {
    background: #6C3FC5;
    color: #fff;
    transform: scale(1.1);
  }
  .cms-view-cell-text {
    font-size: 13px;
    font-weight: 600;
    color: #3D1F8F;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .cms-view-icon {
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    border-radius: 6px;
    background: #EDE8FA;
    color: #6C3FC5;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    transition: background 0.15s, color 0.15s, transform 0.15s;
  }
`;

export function Table({ columns, data, actions, onCellClick }) {
  return (
    <div>
      <style>{TABLE_STYLES}</style>
      <div className="cms-table-outer">
        <table style={{
          width: '100%',
          borderCollapse: 'separate',
          borderSpacing: 0,
          minWidth: 'max-content',
        }}>
          <thead>
            <tr>
              {/* ── Actions header — sticky left ── */}
              {actions && (
                <th style={{
                  padding: '13px 18px',
                  textAlign: 'left',
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#9589B8',
                  textTransform: 'uppercase',
                  letterSpacing: 0.8,
                  background: '#F5F3FC',
                  borderBottom: '2px solid #EDE8FA',
                  borderRight: '1px solid #EDE8FA',
                  whiteSpace: 'nowrap',
                  position: 'sticky',
                  left: 0,
                  top: 0,
                  zIndex: 4,
                }}>
                  Actions
                </th>
              )}
              {columns.map((col) => (
                <th key={col.key} style={{
                  padding: '13px 18px',
                  textAlign: 'left',
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#9589B8',
                  textTransform: 'uppercase',
                  letterSpacing: 0.8,
                  background: '#F5F3FC',
                  borderBottom: '2px solid #EDE8FA',
                  whiteSpace: 'nowrap',
                  userSelect: 'none',
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                }}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (actions ? 1 : 0)} style={{ padding: '64px 16px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 56, height: 56, borderRadius: 16, background: '#F5F3FC', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26 }}>
                      📭
                    </div>
                    <div style={{ color: '#9589B8', fontSize: 14, fontWeight: 500 }}>No records found</div>
                    <div style={{ color: '#C4B5F4', fontSize: 12 }}>Try adjusting your filters or add a new record</div>
                  </div>
                </td>
              </tr>
            ) : (
              data.map((row, i) => {
                const rowBg = i % 2 === 0 ? '#FFFFFF' : '#FDFCFF';
                return (
                  <tr key={row.id ?? i} className="cms-tr">
                    {/* ── Actions cell — sticky left ── */}
                    {actions && (
                      <td className="sticky-action" style={{
                        padding: '12px 18px',
                        background: rowBg,
                        borderBottom: '1px solid #F0EBF8',
                        borderRight: '1px solid #EDE8FA',
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        boxShadow: '6px 0 12px rgba(108,63,197,0.06)',
                        transition: 'background 0.15s ease',
                      }}>
                        <div style={{ display: 'flex', gap: 8 }}>
                          {actions(row)}
                        </div>
                      </td>
                    )}

                    {/* ── Data cells ── */}
                    {columns.map((col) => {
                      const isClickable = col.viewEnable && onCellClick;
                      const rawValue = row[col.key];
                      const rendered = col.render
                        ? col.render(rawValue, row)
                        : (rawValue ?? <span style={{ color: '#C4B5F4' }}>—</span>);

                      return (
                        <td
                          key={col.key}
                          className="cms-td"
                          style={{
                            padding: '10px 18px',
                            fontSize: 13,
                            color: C.text,
                            background: rowBg,
                            borderBottom: '1px solid #F0EBF8',
                            whiteSpace: col.wrap ? 'normal' : 'nowrap',
                            maxWidth: col.wrap ? 220 : undefined,
                            overflow: col.wrap ? 'hidden' : undefined,
                            textOverflow: col.wrap ? 'ellipsis' : undefined,
                            verticalAlign: 'middle',
                            transition: 'background 0.15s ease',
                          }}
                        >
                          {isClickable ? (
                            <span
                              className="cms-view-cell"
                              onClick={() => onCellClick(col, row)}
                              title="Click to view details"
                            >
                              <span className="cms-view-cell-text">{rendered}</span>
                            </span>
                          ) : rendered}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}